import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { db, storage } from '@/lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const HeroSection = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { t } = useTranslation();
  const { role } = useAuth();
  
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  
  const [heroData, setHeroData] = useState({
    line1: '',
    line2: '',
    line3: '',
    description: '',
    btnStart: '',
    btnServices: '',
    imageUrl: 'https://readdy.ai/api/search-image?query=latina%20entrepreneur%20woman%20working%20on%20laptop%20with%20digital%20marketing%20dashboard%20glowing%20screens%20purple%20blue%20neon%20lights%20modern%20office%20futuristic%20aesthetic%20professional%20confident&width=500&height=400&seq=hero1&orientation=landscape'
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: { x: number; y: number; vx: number; vy: number; size: number; opacity: number }[] = [];
    for (let i = 0; i < 80; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.5 + 0.1,
      });
    }

    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(140, 82, 255, ${p.opacity})`;
        ctx.fill();
      });
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach((p2) => {
          const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(0, 194, 255, ${0.08 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });
      animId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setHeroData({
      line1: t('home.hero.title.line1'),
      line2: t('home.hero.title.line2'),
      line3: t('home.hero.title.line3'),
      description: t('home.hero.description'),
      btnStart: t('home.hero.buttons.start'),
      btnServices: t('home.hero.buttons.services'),
      imageUrl: 'https://readdy.ai/api/search-image?query=latina%20entrepreneur%20woman%20working%20on%20laptop%20with%20digital%20marketing%20dashboard%20glowing%20screens%20purple%20blue%20neon%20lights%20modern%20office%20futuristic%20aesthetic%20professional%20confident&width=500&height=400&seq=hero1&orientation=landscape'
    });

    const fetchHeroData = async () => {
      try {
        const docRef = doc(db, 'landing_content', 'hero');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setHeroData(docSnap.data() as typeof heroData);
        }
      } catch (error) {
        console.error('Error fetching hero data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHeroData();
  }, [t]);

  const handleSave = async () => {
    setUploading(true);
    try {
      let imageUrl = heroData.imageUrl;

      if (imageFile) {
        const storageRef = ref(storage, `landing/hero_${Date.now()}_${imageFile.name}`);
        const snapshot = await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(snapshot.ref);
      }

      const dataToSave = { ...heroData, imageUrl };
      await setDoc(doc(db, 'landing_content', 'hero'), dataToSave);
      setHeroData(dataToSave);
      setIsEditing(false);
      setImageFile(null);
    } catch (error) {
      console.error('Error saving hero data:', error);
      alert('Error al guardar los cambios.');
    } finally {
      setUploading(false);
    }
  };

  const isAdmin = role === 'admin';

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#050008]">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0d0020] via-[#050008] to-[#000d1a]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(140,82,255,0.25)_0%,transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_80%,rgba(0,194,255,0.15)_0%,transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,rgba(255,45,170,0.05)_0%,transparent_70%)]" />

      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'linear-gradient(rgba(140,82,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(140,82,255,0.3) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* Botones de Control para Admin */}
      {isAdmin && (
        <div className="absolute top-24 right-4 z-50 flex gap-2">
          {!isEditing ? (
            <button 
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
            >
              <i className="ri-edit-line" />
              Editar Contenido
            </button>
          ) : (
            <>
              <button 
                onClick={handleSave}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
                disabled={uploading}
              >
                {uploading ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-save-line" />
                )}
                {uploading ? "Guardando..." : "Guardar"}
              </button>
              <button 
                onClick={() => { setIsEditing(false); setImageFile(null); }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
                disabled={uploading}
              >
                <i className="ri-close-line" />
                Cancelar
              </button>
            </>
          )}
        </div>
      )}

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-28 pb-20 flex flex-col lg:flex-row items-center gap-12">
        <div className="flex-1 text-center lg:text-left">
          <div className="inline-flex items-center gap-2 bg-white/5 border border-[#8c52ff]/30 rounded-full px-4 py-2 mb-8 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-[#8c52ff] animate-pulse" />
            <span className="text-[#8c52ff] text-xs font-semibold tracking-widest uppercase">
              Smart Business Digital Solutions
            </span>
          </div>

          {isEditing ? (
            <div className="space-y-4 mb-6">
              <input 
                type="text" 
                value={heroData.line1} 
                onChange={(e) => setHeroData({...heroData, line1: e.target.value})}
                className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white text-3xl font-bold focus:outline-none focus:border-purple-500"
                placeholder="Línea 1"
              />
              <input 
                type="text" 
                value={heroData.line2} 
                onChange={(e) => setHeroData({...heroData, line2: e.target.value})}
                className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white text-3xl font-bold focus:outline-none focus:border-purple-500"
                placeholder="Línea 2"
              />
              <input 
                type="text" 
                value={heroData.line3} 
                onChange={(e) => setHeroData({...heroData, line3: e.target.value})}
                className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white text-2xl font-bold focus:outline-none focus:border-purple-500"
                placeholder="Línea 3"
              />
            </div>
          ) : (
            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-tight mb-6">
              <span className="block">{heroData.line1}</span>
              <span className="block bg-gradient-to-r from-[#8c52ff] via-[#FF2DAA] to-[#00c2ff] bg-clip-text text-transparent">
                {heroData.line2}
              </span>
              <span className="block text-3xl md:text-4xl lg:text-5xl font-bold text-white/90 mt-2">
                {heroData.line3}
              </span>
            </h1>
          )}

          {isEditing ? (
            <textarea 
              value={heroData.description} 
              onChange={(e) => setHeroData({...heroData, description: e.target.value})}
              className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white text-base focus:outline-none focus:border-purple-500 mb-6 h-32"
              placeholder="Descripción..."
            />
          ) : (
            <p className="text-white/60 text-base md:text-lg lg:text-xl max-w-xl mx-auto lg:mx-0 mb-10 leading-relaxed">
              {heroData.description}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            {isEditing ? (
              <div className="flex gap-2 w-full sm:w-auto">
                <input 
                  type="text" 
                  value={heroData.btnStart} 
                  onChange={(e) => setHeroData({...heroData, btnStart: e.target.value})}
                  className="bg-white/5 border border-purple-500/50 rounded-full px-4 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                  placeholder="Texto Botón 1"
                />
                <input 
                  type="text" 
                  value={heroData.btnServices} 
                  onChange={(e) => setHeroData({...heroData, btnServices: e.target.value})}
                  className="bg-white/5 border border-purple-500/50 rounded-full px-4 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                  placeholder="Texto Botón 2"
                />
              </div>
            ) : (
              <>
                <a
                  href="https://smartbusiness.academy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#8c52ff] to-[#FF2DAA] text-white font-bold px-8 py-4 rounded-full hover:opacity-90 hover:scale-105 transition-all duration-300 text-base shadow-lg shadow-purple-500/30 cursor-pointer"
                >
                  <i className="ri-rocket-line" />
                  {heroData.btnStart}
                </a>
                <Link
                  to="/services"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-white/5 backdrop-blur-sm border border-white/20 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 hover:border-[#00c2ff]/50 transition-all duration-300 text-base cursor-pointer"
                >
                  <i className="ri-compass-3-line" />
                  {heroData.btnServices}
                </Link>
              </>
            )}
          </div>

          <div className="flex flex-wrap gap-6 mt-12 justify-center lg:justify-start">
            {[
              { value: '50+', label: t('home.hero.miniStats.students') },
              { value: '200+', label: t('home.hero.miniStats.clients') },
              { value: '8+', label: t('home.hero.miniStats.years') },
            ].map((stat) => (
              <div key={stat.label} className="text-center lg:text-left">
                <div className="text-2xl font-black bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-white/40 text-xs uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Visual */}
        <div className="flex-1 flex items-center justify-center relative">
          <div className="relative w-full max-w-md">
            <div className="absolute inset-0 bg-gradient-to-br from-[#8c52ff]/30 to-[#00c2ff]/20 rounded-3xl blur-3xl scale-110" />
            <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#8c52ff] via-[#FF2DAA] to-[#00c2ff]" />
              <img
                src={heroData.imageUrl}
                alt="Digital marketing entrepreneur"
                className="w-full h-56 object-cover object-top rounded-2xl mb-4"
              />
              
              {isEditing && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70 rounded-2xl mb-4">
                  <div className="w-4/5 text-center p-4">
                    <p className="text-white text-xs mb-2">Sube una nueva imagen:</p>
                    <input 
                      type="file" 
                      accept="image/*"
                      onChange={(e) => setImageFile(e.target.files ? e.target.files[0] : null)}
                      className="w-full bg-white/10 border border-purple-500/50 rounded px-2 py-1 text-white text-xs focus:outline-none mb-2"
                    />
                    <p className="text-white/50 text-xs">O pega la URL:</p>
                    <input 
                      type="text" 
                      value={heroData.imageUrl} 
                      onChange={(e) => setHeroData({...heroData, imageUrl: e.target.value})}
                      className="w-full bg-white/10 border border-purple-500/50 rounded px-2 py-1 text-white text-xs focus:outline-none"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              )}

              <div className="absolute top-10 -right-4 bg-black/80 backdrop-blur-xl border border-[#8c52ff]/40 rounded-2xl px-4 py-3 shadow-xl">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#8c52ff]/20">
                    <i className="ri-line-chart-line text-[#8c52ff] text-sm" />
                  </div>
                  <div>
                    <div className="text-white text-xs font-bold">+340%</div>
                    <div className="text-white/40 text-xs">Crecimiento</div>
                  </div>
                </div>
              </div>
              <div className="absolute bottom-24 -left-4 bg-black/80 backdrop-blur-xl border border-[#00c2ff]/40 rounded-2xl px-4 py-3 shadow-xl">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#00c2ff]/20">
                    <i className="ri-robot-line text-[#00c2ff] text-sm" />
                  </div>
                  <div>
                    <div className="text-white text-xs font-bold">IA Activa</div>
                    <div className="text-white/40 text-xs">Automatización</div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: 'ri-instagram-line', label: 'Instagram', color: '#FF2DAA' },
                  { icon: 'ri-tiktok-line', label: 'TikTok', color: '#00c2ff' },
                  { icon: 'ri-facebook-circle-line', label: 'Facebook', color: '#8c52ff' },
                ].map((s) => (
                  <div key={s.label} className="bg-white/5 rounded-xl p-3 text-center border border-white/10">
                    <i className={`${s.icon} text-xl`} style={{ color: s.color }} />
                    <div className="text-white/50 text-xs mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/30 text-xs tracking-widest uppercase">Scroll</span>
        <i className="ri-arrow-down-line text-white/30" />
      </div>
    </section>
  );
};

export default HeroSection;
